/**
 * @fileoverview The short bullets in every first-time "make public" dialog (three, and a fourth for a plant).
 *
 * One list for the plant, the bed and the season, so the three dialogs say the
 * same things in the same order:
 * 1. where it goes: the gardener's own garden page, with its link (a tappable
 *    link opening in a new tab; without a page address yet, "on your garden
 *    page" and no link);
 * 2. what the pages show: the gardener's name (a plant's also says its photos
 *    may appear on our websites once looked at);
 * 3. that it can be switched off, and where the page's address and shown name
 *    change (Profile > Public pages);
 * 4. a plant's only: that its saved Plant Doctor checkups show too, and any
 *    one can be hidden.
 *
 * The list is the dialog's description (DialogDescription, rendered as the
 * list itself), so it must sit inside a Dialog's content.
 *
 * Where: PublicPageSwitch (plant, through GalleryConsentDialog, and bed) and
 * SeasonReviewPage (season).
 * API: GET /gardens/mine (getMyGarden), the same source as Profile > Public
 * pages; the link is built with gardenUrlFor from its code and address.
 * Settings: MY_GARDEN_QUERY_KEY, MY_GARDEN_STALE_MS (config/tunables/garden).
 * Words (en/es): publicConsent.*.
 *
 * @module PublicConsentBullets
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { DialogDescription } from '@/components/ui/dialog';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { getMyGarden, gardenUrlFor } from '@/shared/api/gardens';
import { MY_GARDEN_QUERY_KEY, MY_GARDEN_STALE_MS } from '@/config/tunables/garden';

/** What the dialog is for, which words the second bullet uses. */
interface PublicConsentBulletsProps {
  /** A plant (adds the websites sentence), a bed, or the season. */
  kind: 'plant' | 'bed' | 'season';
}

/**
 * The bullets, as the dialog's description.
 *
 * @param props - Which kind of thing is going public.
 * @returns The list.
 */
export const PublicConsentBullets: React.FC<PublicConsentBulletsProps> = ({ kind }) => {
  const { reactTranslate: t } = useTranslation();
  const { data: garden } = useQuery({ queryKey: [MY_GARDEN_QUERY_KEY], queryFn: getMyGarden, staleTime: MY_GARDEN_STALE_MS });
  /** The link as Profile > Public pages shows it; null while it loads or when there is no page yet. */
  const url = garden ? gardenUrlFor(garden.code, garden.slug) : null;
  const second = kind === 'plant' ? 'publicConsent.namePhotos' : kind === 'season' ? 'publicConsent.seasonName' : 'publicConsent.name';

  return (
    <DialogDescription asChild>
      <ul className="list-disc space-y-2 pl-5 text-left">
        <li>
          {url ? (
            <>
              {t('publicConsent.gardenLink')}{' '}
              <a href={url} target="_blank" rel="noopener noreferrer" className="break-all font-medium text-primary underline underline-offset-2">
                {url.replace(/^https?:\/\//, '')}
              </a>
            </>
          ) : t('publicConsent.gardenPage')}
        </li>
        <li>{t(second)}</li>
        <li>{t('publicConsent.control')}</li>
        {kind === 'plant' && <li>{t('publicConsent.checkups')}</li>}
      </ul>
    </DialogDescription>
  );
};
